import {RemoteConstantsModel} from "../models/remoteConstants.model";

// The attribute ids an attribute TYPE requires, as the Client declares them with
// [AttributeRequires] (the attribute manifest's `Requires` — every spell requires
// Spellcaster). Callers pass one built over the loaded manifest; without a manifest
// only the hand-authored `requires` on the metadata counts.
export type ManifestRequires = (attributeId: string) => string[];

// Given a unit and one attribute uid it has been assigned, return the list of
// attribute IDs (not uids) that this attribute requires but the unit is
// missing. Empty array means dependencies are satisfied.
//
// The dependency is the union of the metadata's own `requires` and what the
// attribute's type requires in the manifest — so a spell instance created without
// `requires` still warns when its unit has no Spellcaster. Single point of truth
// shared between the inline editor warning and the export warning pass.
export function getMissingUnitAttributeDependencies(
    unitAttributeUids: string[],
    attrUid: string,
    state: RemoteConstantsModel,
    manifestRequires?: ManifestRequires,
): string[] {
    const meta = state.unitAttributes?.find(m => m.uid === attrUid);
    if (!meta) return [];

    const required = Array.from(new Set([...(meta.requires ?? []), ...(manifestRequires?.(meta.id) ?? [])]));
    if (required.length === 0) return [];

    const presentIds = new Set<string>(
        unitAttributeUids
            .map(uid => state.unitAttributes?.find(m => m.uid === uid)?.id)
            .filter((id): id is string => !!id)
    );

    return required.filter(id => !presentIds.has(id));
}

// Walk every unit and return one warning string per (unit, attribute,
// missingDependency) triple. Used by the export pre-flight.
export function getUnitAttributeDependencyWarnings(
    state: RemoteConstantsModel,
    name: (id: string) => string,
    manifestRequires?: ManifestRequires,
): string[] {
    const warnings: string[] = [];
    for (const unit of state.units ?? []) {
        for (const attrUid of unit.attributes ?? []) {
            const missing = getMissingUnitAttributeDependencies(unit.attributes, attrUid, state, manifestRequires);
            if (missing.length === 0) continue;
            const attrMeta = state.unitAttributes?.find(m => m.uid === attrUid);
            const attrName = attrMeta ? name(attrMeta.uid) : attrUid;
            warnings.push(
                `Unit "${name(unit.id)}" has "${attrName}" but is missing required attribute${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`
            );
        }
    }
    return warnings;
}

export function getMissingDependencyTooltip(missing: string[]): string {
    return `Requires: ${missing.join(', ')} (not present on this unit)`;
}

export interface DependencyLookup {
    (uid: string): string[]; // returns missing dep IDs for that attribute uid
}

// Builds the lookup once per render so each MetadataLabel doesn't re-scan
// state.unitAttributes for every uid in the list.
export function buildUnitDependencyLookup(
    unitAttributeUids: string[],
    state: RemoteConstantsModel,
    manifestRequires?: ManifestRequires,
): DependencyLookup {
    return (uid: string) => getMissingUnitAttributeDependencies(unitAttributeUids, uid, state, manifestRequires);
}
